import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

export type UVHit = { x: number; y: number; isPointOnSurface: boolean };

export interface View3DOptions {
  domContainer: HTMLElement;
  texture: THREE.Texture;
  matcapUrl?: string;
  normalMapUrl?: string;
}

export interface View3D {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  setModel(obj: THREE.Object3D): Promise<void>;
  screenToUV(clientX: number, clientY: number): UVHit;
  setFrontView(): void;
  setBackView(): void;
  smoothRotateTo(deg: number): void;
  playIntroSpin(): void;
  captureFrontBack(): Promise<{ frontImage: string; backImage: string } | null>;
  freeze(frozen: boolean): void;
  resize(): void;
  dispose(): void;
  getModel(): THREE.Object3D | null;
}

export async function loadGLTF(url: string): Promise<THREE.Group> {
  // Assets in public/assets/glb are uncompressed glTF2 (no Draco/meshopt).
  // Do not attach DRACOLoader — it can stall waiting on a CDN decoder.
  const loader = new GLTFLoader();

  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error(`Timed out loading model: ${url}`));
    }, 45_000);

    loader.load(
      url,
      gltf => {
        window.clearTimeout(timer);
        resolve(gltf.scene);
      },
      undefined,
      err => {
        window.clearTimeout(timer);
        reject(err instanceof Error ? err : new Error(String(err)));
      },
    );
  });
}

export function createView3D(opts: View3DOptions): View3D {
  const { domContainer, texture } = opts;
  // StrictMode / re-init: never stack renderers — drop any previous canvases
  for (const old of Array.from(domContainer.querySelectorAll('canvas'))) {
    old.remove();
  }
  const width = domContainer.clientWidth || 1;
  const height = domContainer.clientHeight || 1;

  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: true,
    preserveDrawingBuffer: true,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(width, height);
  renderer.domElement.id = 'viewport';
  renderer.domElement.style.display = 'block';
  renderer.domElement.style.width = '100%';
  renderer.domElement.style.height = '100%';
  domContainer.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
  camera.position.set(0, 0.2, 2.5);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.enablePan = false;
  controls.enableZoom = true;
  controls.rotateSpeed = 0.9;
  controls.zoomSpeed = 0.6;
  controls.minPolarAngle = Math.PI * 0.15;
  controls.maxPolarAngle = Math.PI * 0.75;

  const pivot = new THREE.Object3D();
  scene.add(pivot);

  let model: THREE.Object3D | null = null;
  let frozen = false;
  let introUntil = 0;

  const raycaster = new THREE.Raycaster();
  const ndc = new THREE.Vector2();

  texture.flipY = false;
  texture.generateMipmaps = false;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = renderer.capabilities.getMaxAnisotropy();

  let matcapTex: THREE.Texture | null = null;
  if (opts.matcapUrl) {
    matcapTex = new THREE.TextureLoader().load(opts.matcapUrl);
  }
  let normalTex: THREE.Texture | null = null;
  if (opts.normalMapUrl) {
    normalTex = new THREE.TextureLoader().load(opts.normalMapUrl, t => {
      t.flipY = false;
    });
  }

  const designMat = new THREE.MeshMatcapMaterial({
    side: THREE.FrontSide,
    map: texture,
    matcap: matcapTex ?? undefined,
    normalMap: normalTex,
  });
  const backMat = new THREE.MeshMatcapMaterial({
    color: 0xcccccc,
    side: THREE.BackSide,
    matcap: matcapTex ?? undefined,
    normalMap: normalTex,
  });

  function applyMaterials(root: THREE.Object3D) {
    let first = true;
    root.traverse(obj => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;
      if (first) {
        first = false;
        mesh.material = designMat;
        const shell = mesh.clone() as THREE.Mesh;
        shell.material = backMat;
        shell.name = '__back_shell__';
        mesh.parent?.add(shell);
      } else {
        const src = Array.isArray(mesh.material)
          ? (mesh.material[0] as THREE.MeshStandardMaterial)
          : (mesh.material as THREE.MeshStandardMaterial);
        // Prefer the live design texture so every garment mesh shows the SVG design.
        // Keep an embedded GLB map only if present (rare); never leave a bare matcap.
        const keepMap = src?.map ?? null;
        mesh.material = new THREE.MeshMatcapMaterial({
          side: THREE.DoubleSide,
          map: keepMap ?? texture,
          matcap: matcapTex ?? undefined,
          normalMap: normalTex ?? src?.normalMap ?? null,
          color: 0xffffff,
        });
        (mesh.material as THREE.Material).needsUpdate = true;
      }
    });
  }

  function fitCamera(obj: THREE.Object3D) {
    const box = new THREE.Box3().setFromObject(obj);
    if (box.isEmpty()) return;
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    const fov = (camera.fov * Math.PI) / 180;
    const dist = Math.max(sphere.radius / Math.sin(fov / 2), 0.5) * 1.15;
    controls.target.copy(sphere.center);
    camera.position.set(
      sphere.center.x,
      sphere.center.y + sphere.radius * 0.1,
      sphere.center.z + dist,
    );
    camera.near = dist / 100;
    camera.far = dist * 50;
    camera.updateProjectionMatrix();
    controls.update();
  }

  async function setModel(obj: THREE.Object3D) {
    const t0 = performance.now();
    let meshCount = 0;
    obj.traverse(o => {
      if ((o as THREE.Mesh).isMesh) meshCount += 1;
    });
    console.info('[setModel] begin', { meshCount });
    if (model) {
      pivot.remove(model);
      model.traverse(o => {
        const m = o as THREE.Mesh;
        if (m.isMesh && m.name !== '__back_shell__') m.geometry?.dispose();
      });
    }
    applyMaterials(obj);
    model = obj;
    pivot.add(obj);
    fitCamera(obj);
    console.info('[setModel] ok', {
      ms: Math.round(performance.now() - t0),
      camera: camera.position.toArray().map(n => +n.toFixed(3)),
      target: controls.target.toArray().map(n => +n.toFixed(3)),
    });
  }

  function screenToUV(clientX: number, clientY: number): UVHit {
    if (!model) return { x: 0, y: 0, isPointOnSurface: false };
    const rect = renderer.domElement.getBoundingClientRect();
    ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(ndc, camera);
    const hits = raycaster.intersectObject(model, true);
    const hit = hits.find(h => h.uv);
    if (hit?.uv) {
      return { x: hit.uv.x, y: hit.uv.y, isPointOnSurface: true };
    }
    return { x: 0, y: 0, isPointOnSurface: false };
  }

  function applyView(deg: number) {
    const r = camera.position.distanceTo(controls.target);
    const rad = THREE.MathUtils.degToRad(deg);
    camera.position.set(
      controls.target.x + Math.sin(rad) * r,
      controls.target.y + r * 0.12,
      controls.target.z + Math.cos(rad) * r,
    );
    camera.lookAt(controls.target);
    controls.update();
    console.info('[view3d] applyView', deg, {
      camera: camera.position.toArray().map(n => +n.toFixed(3)),
    });
  }

  function capturePair(frontDeg: number, backDeg: number) {
    if (!model) return null;
    const prevPos = camera.position.clone();
    const prevTarget = controls.target.clone();
    const prevFov = camera.fov;
    const prevNear = camera.near;
    const prevFar = camera.far;

    const box = new THREE.Box3().setFromObject(pivot);
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    const fov = (prevFov * Math.PI) / 180;
    const aspect = camera.aspect;
    const dist =
      Math.max(
        sphere.radius / Math.sin(fov / 2),
        sphere.radius / Math.sin(Math.atan(Math.tan(fov / 2) * aspect)),
      ) * 1.2;

    camera.near = dist / 100;
    camera.far = dist * 100;
    camera.updateProjectionMatrix();
    controls.enabled = false;

    const shoot = (deg: number) => {
      const rad = THREE.MathUtils.degToRad(deg);
      camera.position.set(
        sphere.center.x + Math.sin(rad) * dist,
        sphere.center.y + dist * 0.05,
        sphere.center.z + Math.cos(rad) * dist,
      );
      camera.lookAt(sphere.center);
      renderer.render(scene, camera);
      return renderer.domElement.toDataURL('image/png');
    };

    const frontImage = shoot(frontDeg);
    const backImage = shoot(backDeg);

    camera.position.copy(prevPos);
    controls.target.copy(prevTarget);
    camera.fov = prevFov;
    camera.near = prevNear;
    camera.far = prevFar;
    camera.updateProjectionMatrix();
    controls.enabled = !frozen;
    controls.update();
    renderer.render(scene, camera);

    return { frontImage, backImage };
  }

  const ro = new ResizeObserver(() => resize());
  ro.observe(domContainer);

  function resize() {
    const w = domContainer.clientWidth;
    const h = domContainer.clientHeight;
    if (!w || !h) return;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
  }

  let raf = 0;
  let introSnapped = true;
  function tick() {
    raf = requestAnimationFrame(tick);
    const inIntro = introUntil > performance.now();
    if (inIntro && controls.enabled) {
      controls.autoRotate = true;
      controls.autoRotateSpeed = 8;
      introSnapped = false;
    } else {
      controls.autoRotate = false;
      // Intro ends mid-spin — snap back to front so we don't freeze on a side view
      if (!introSnapped && introUntil > 0) {
        introSnapped = true;
        applyView(0);
      }
    }
    controls.update();
    renderer.render(scene, camera);
  }
  tick();

  return {
    renderer,
    scene,
    camera,
    controls,
    setModel,
    screenToUV,
    setFrontView: () => applyView(0),
    setBackView: () => applyView(180),
    smoothRotateTo: deg => applyView(deg),
    playIntroSpin: () => {
      introUntil = performance.now() + 1400;
      introSnapped = false;
      console.info('[view3d] intro spin 1.4s → will snap to front');
    },
    captureFrontBack: async () => capturePair(0, 180),
    freeze: f => {
      frozen = f;
      controls.enabled = !f;
      renderer.domElement.style.cursor = f ? 'crosshair' : '';
    },
    resize,
    getModel: () => model,
    dispose: () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      controls.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
}
